import React from 'react';
import { Card, CardContent, Box, Skeleton, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme, isMobile, isCampaign }) => ({
  width: isMobile ? '100%' : isCampaign ? '417px' : '417px',
  background: '#FFFFFF',
  boxShadow: '0px 10px 26px rgba(0, 0, 0, 0.04)',
  borderRadius: isMobile ? '12px' : '16px',
  overflow: 'visible',
  fontFamily: "'Red Hat Display', sans-serif",
  maxWidth: isMobile ? '100%' : isCampaign ? '100%' : '100%',
}));

const ImageSection = styled(Box)(({ theme, isMobile }) => ({
  width: '100%',
  position: 'relative',
  borderRadius: isMobile ? '12px' : '16px',
  overflow: 'hidden',
  marginBottom: isMobile ? '14px' : '18px',
}));

const ProgressContainer = styled(Box)(({ theme, isMobile }) => ({
  position: 'relative',
  marginBottom: isMobile ? '40px' : '50px',
}));

const StatsContainer = styled(Box)(({ theme, isMobile }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: isMobile ? '16px' : '20px',
  gap: isMobile ? '6px' : '8px',
  marginTop: isMobile ? '8px' : '10px',
}));

const StatItem = styled(Box)(({ theme, isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '8px' : '10px',
  height: isMobile ? '32px' : '38px',
  flex: 1,
  minWidth: 0,
}));

const CampaignCardSkeleton = ({ isCampaign = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box>
      <StyledCard isMobile={isMobile} isCampaign={isCampaign}>
        <CardContent sx={{ 
          padding: isMobile ? '12px' : '16px', 
          '&:last-child': { paddingBottom: isMobile ? '16px' : '20px' } 
        }}>
          {/* Image Section Skeleton */}
          <ImageSection isMobile={isMobile}>
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height={isMobile ? '156px' : '217px'} 
              sx={{ borderRadius: isMobile ? '12px' : '16px' }}
              animation="wave"
            />
            {/* Tax Benefit Chip Skeleton */}
            <Skeleton
              variant="rectangular"
              width={isMobile ? '60px' : '70px'}
              height={isMobile ? '20px' : '24px'}
              sx={{
                position: 'absolute',
                top: isMobile ? '8px' : '10px',
                right: isMobile ? '8px' : '10px',
                borderRadius: '4px',
              }}
              animation="wave"
            />
          </ImageSection>

          {/* Main Content */}
          <Box sx={{ width: '100%' }}>
            {/* Title Skeleton */}
            <Skeleton
              variant="text"
              width="100%"
              height={isMobile ? '36px' : '52px'}
              sx={{ marginBottom: isMobile ? '8px' : '10px' }}
              animation="wave"
            />

            {/* Progress Bar Skeleton */}
            <ProgressContainer isMobile={isMobile}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={isMobile ? '10px' : '12px'}
                sx={{ borderRadius: '20px', marginBottom: '8px' }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width="50px"
                height="20px"
                sx={{ marginLeft: 'auto', marginRight: 'auto' }}
                animation="wave"
              />
            </ProgressContainer>

            {/* Statistics Skeleton */}
            <StatsContainer isMobile={isMobile}>
              <StatItem isMobile={isMobile}>
                <Skeleton
                  variant="circular"
                  width={isMobile ? '28px' : '32px'}
                  height={isMobile ? '28px' : '32px'}
                  animation="wave"
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                  <Skeleton variant="text" width="40px" height={isMobile ? '12px' : '14px'} animation="wave" />
                  <Skeleton variant="text" width="60px" height={isMobile ? '12px' : '14px'} animation="wave" />
                </Box>
              </StatItem>

              <Box sx={{ width: '1px', height: isMobile ? '32px' : '38px', background: '#E8E8E8' }} />

              <StatItem isMobile={isMobile} sx={{ justifyContent: 'center' }}>
                <Skeleton
                  variant="circular"
                  width={isMobile ? '28px' : '32px'}
                  height={isMobile ? '28px' : '32px'}
                  animation="wave"
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                  <Skeleton variant="text" width="50px" height={isMobile ? '12px' : '14px'} animation="wave" />
                  <Skeleton variant="text" width="70px" height={isMobile ? '12px' : '14px'} animation="wave" />
                </Box>
              </StatItem>

              <Box sx={{ width: '1px', height: isMobile ? '32px' : '38px', background: '#E8E8E8' }} />

              <StatItem isMobile={isMobile} sx={{ justifyContent: 'flex-end' }}>
                <Skeleton
                  variant="circular"
                  width={isMobile ? '28px' : '32px'}
                  height={isMobile ? '28px' : '32px'}
                  animation="wave"
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                  <Skeleton variant="text" width="50px" height={isMobile ? '12px' : '14px'} animation="wave" />
                  <Skeleton variant="text" width="40px" height={isMobile ? '12px' : '14px'} animation="wave" />
                </Box>
              </StatItem>
            </StatsContainer>

            {/* Divider Skeleton */}
            <Box sx={{ marginBottom: isMobile ? '16px' : '20px' }}>
              <Skeleton variant="rectangular" width="100%" height="1px" animation="wave" />
            </Box>

            {/* Footer Skeleton */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                height: isMobile ? '32px' : '44px',
              }}
            >
              {/* Social Media Icons Skeleton */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
                <Skeleton
                  variant="rectangular"
                  width={isMobile ? '28px' : '40px'}
                  height={isMobile ? '28px' : '40px'}
                  sx={{ borderRadius: '10px' }}
                  animation="wave"
                />
                <Skeleton
                  variant="rectangular"
                  width={isMobile ? '28px' : '40px'}
                  height={isMobile ? '28px' : '40px'}
                  sx={{ borderRadius: '10px' }}
                  animation="wave"
                />
                <Skeleton
                  variant="rectangular"
                  width={isMobile ? '28px' : '40px'}
                  height={isMobile ? '28px' : '40px'}
                  sx={{ borderRadius: '10px' }}
                  animation="wave"
                />
              </Box>

              {/* Donate Button Skeleton */}
              <Skeleton
                variant="rectangular"
                width={isMobile ? '80px' : '100px'}
                height={isMobile ? '32px' : '40px'}
                sx={{ borderRadius: '10px' }}
                animation="wave"
              />
            </Box>
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default CampaignCardSkeleton;

